import { ReactNode } from 'react';
import { COLLECTION_VIEW_FIELD_ORDER, useFeatureFlag } from '@shared/util/featureFlags';
import {
  ViewLayoutActionsProvider,
  ViewLayoutStateProvider,
  useViewLayoutState,
} from './ViewLayoutContext';
import { usePreserveEdits, useViewTemplateState } from './ViewTemplateContext';
import { useViewLayoutDraft } from './useViewLayoutDraft';

interface ViewLayoutProviderProps {
  children: ReactNode;
  workflowTemplateId: string;
  viewTemplateId: string;
}

/**
 * Loads the layout a collection view orders its fields by: which fields show is the view
 * template's call, and the layout only decides the order of the ones that do. Holds the unsaved
 * changes to that order too, kept and restored with the view template's own when it preserves
 * edits.
 *
 * Mounted inside a ViewTemplateProvider, only where a view's cards render, so nothing else that
 * holds a view template, like the filter menu, asks for a layout.
 *
 * With field ordering flagged off there is no provider at all: the context defaults give no
 * layout and no-op actions, so fields follow workflow template order and no order is saved.
 */
export function ViewLayoutProvider(props: ViewLayoutProviderProps) {
  const isFieldOrderEnabled = useFeatureFlag(COLLECTION_VIEW_FIELD_ORDER);
  if (!isFieldOrderEnabled) {
    return <>{props.children}</>;
  }
  return <FieldOrderLayoutProvider {...props} />;
}

function FieldOrderLayoutProvider({
  children,
  workflowTemplateId,
  viewTemplateId,
}: ViewLayoutProviderProps) {
  const { viewTemplate, savedViewTemplate } = useViewTemplateState();
  const preserveEdits = usePreserveEdits();
  const { state, actions } = useViewLayoutDraft({
    workflowTemplateId,
    viewTemplateId,
    viewTemplate,
    savedViewTemplate,
    preserveEdits,
  });

  return (
    <ViewLayoutStateProvider value={state}>
      <ViewLayoutActionsProvider value={actions}>{children}</ViewLayoutActionsProvider>
    </ViewLayoutStateProvider>
  );
}

/**
 * Holds the cards back until the layout is in, so their fields don't render once in workflow
 * template order and then jump into place. Wraps only the cards, so the rest of the view renders
 * without waiting.
 */
export function WaitForViewLayout({ children }: { children: ReactNode }) {
  const { isLoading } = useViewLayoutState();

  return isLoading ? null : <>{children}</>;
}
